import { Radio } from 'antd';
import { EllipsisVertical } from 'lucide-react';

import { Flex, VFlex } from '@/components/base';
import IconButton from '@/components/Button/IconButton';
import Menu from '@/components/Menu/Menu';
import Modal from '@/components/Modal/Modal';
import {
  CHART_GRAPH,
  CHART_METRIC,
  CHART_METRIC_VIEW_TYPE,
  CHART_SHARE_CALCULATION_TYPE,
  CHART_TERM_TYPE,
} from '@/entities/chart/model/constants';
import useChartStateAction from '@/features/(charts)/chart-controller/hooks/use-chart-state-action/useChartStateAction';
import {
  isCalculationTypeEnabled,
  isCellCalculationSegmentEnabled,
  isViewTypeEnabled,
} from '@/features/(charts)/chart-renderer/lib/conditions';
import { ChartTermTypeMap } from '@/features/(charts)/chart-renderer/model/chart-maps';
import ColorScaleModal from '@/features/(charts)/chart-renderer/ui/chart-setting-modal/ColorScaleModal';
import ScaleOptionModal from '@/features/(charts)/chart-renderer/ui/chart-setting-modal/ScaleOptionModal';
import useModalState from '@/hooks/pure/useModalState';
import type { FeedbackSentiment } from '@/types/feedbackConst';

import { CalculationSelect } from '../components/CalculationSelect';
import { CellCalculationSelect } from '../components/CellCalculationSelect';
import { ChartDimensionSelect } from '../components/chart-dimension-select';
import ChartComboValidateDisplay from '../components/ChartComboValidateDisplay';
import ChartDataFilter from '../components/ChartDataFilter';
import ChartMetricSelect from '../components/ChartMetricSelect';
import ChartTermCompare from '../components/ChartTermCompare';
import ChartTermCustomPicker from '../components/ChartTermCustomPicker';
import ChartTermSelect from '../components/ChartTermSelect';
import { Pane } from '../components/ControllerPane';
import TermValidateDisplay from '../components/TermValidateDisplay';
import { ViewTypeSelect } from '../components/ViewTypeSelect';

type Props = {
  chartInfo: ChartContextProps;
  switchCount: number;
};

export const SingleAxisController = (props: Props) => {
  const { chartInfo } = props;
  const { state } = chartInfo;
  const {
    onTermTypeChange,
    onScaleOptionChange,
    onColorScaleChange,
    onViewTypeChange,
    onShareCalculationTypeChange,
    onShareCellCalculationSegmentChange,
  } = useChartStateAction(props.chartInfo);

  const {
    status: scaleOptionModalStatus,
    open: openScaleOptionModal,
    close: closeScaleOptionModal,
    modalState: scaleOptionModalState,
  } = useModalState({
    seriesIndex: 0,
  });

  const {
    status: colorScaleModalStatus,
    open: openColorScaleModal,
    close: closeColorScaleModal,
    modalState: colorScaleModalState,
  } = useModalState({
    seriesIndex: 0,
  });

  const calculationTypeEnabled = isCalculationTypeEnabled(
    state.type,
    state.graph,
    state.series[0].options?.view_type,
  );
  const isWordCloudGraph = state.graph === CHART_GRAPH.WORD_CLOUD;
  const cellCalculationSegmentEnabled = isCellCalculationSegmentEnabled(
    calculationTypeEnabled,
    state.series[0].options?.share_calculation_type,
  );

  return (
    <>
      <Pane
        title="Data set"
        titleAction={
          <Menu
            triggerNode={
              <IconButton
                outlined={false}
                icon={<EllipsisVertical className="size-3 text-gray-700" />}
                sizeType="s"
              />
            }
            options={[
              {
                label: 'Color scale',
                onClick: () =>
                  openColorScaleModal({
                    modalState: { seriesIndex: 0 },
                  }),
                disabled:
                  props.chartInfo.state.graph === CHART_GRAPH.COMBO_CHART ||
                  props.chartInfo.state.graph !== CHART_GRAPH.TABLE,
              },
              {
                label: 'Scale option',
                onClick: () =>
                  openScaleOptionModal({
                    modalState: { seriesIndex: 0 },
                  }),
                disabled:
                  props.chartInfo.state.series[0].options?.view_type ===
                    CHART_METRIC_VIEW_TYPE.PERCENTAGE ||
                  props.chartInfo.state.graph === CHART_GRAPH.COMBO_CHART,
              },
            ]}
          />
        }
      >
        <VFlex g={12}>
          <ChartDimensionSelect
            switchFlag={props.switchCount}
            key={chartInfo.state.type}
            chartInfo={chartInfo}
            allowTop={!isWordCloudGraph}
          />
          {isViewTypeEnabled(state.graph, state.series[0].measure.metric) && (
            <ViewTypeSelect
              value={state.series[0].options?.view_type}
              onChange={(e) =>
                onViewTypeChange(0, e.target.value as CHART_METRIC_VIEW_TYPE)
              }
            />
          )}
          {calculationTypeEnabled && (
            <CalculationSelect
              value={
                state.series[0].options?.share_calculation_type ||
                CHART_SHARE_CALCULATION_TYPE.GRAND_TOTAL
              }
              showCellOption={
                state.series[0].measure.metric !== CHART_METRIC.IMPRESSION
              }
              onChange={(value) => onShareCalculationTypeChange(0, value)}
            />
          )}
          {cellCalculationSegmentEnabled && (
            <CellCalculationSelect
              value={
                state.series[0].options
                  ?.share_cell_calculation_segment as FeedbackSentiment
              }
              onChange={(value) =>
                onShareCellCalculationSegmentChange(0, value)
              }
            />
          )}
        </VFlex>
      </Pane>

      <Pane title="Filter">
        <VFlex g={12}>
          <ChartDataFilter chartInfo={chartInfo} />
        </VFlex>
      </Pane>

      <Pane
        title="Date range"
        titleAction={
          <Flex j="space-between" onClick={(e) => e.stopPropagation()}>
            <Radio.Group
              value={state.target.term.type}
              onChange={(e) => {
                onTermTypeChange(e.target.value as CHART_TERM_TYPE);
              }}
              options={ChartTermTypeMap.toArray()}
            />
          </Flex>
        }
      >
        <VFlex g={12}>
          {state.target.term.type === CHART_TERM_TYPE.PERIODIC && (
            <ChartTermSelect chartInfo={chartInfo} />
          )}
          {state.target.term.type === CHART_TERM_TYPE.FIXED && (
            <ChartTermCustomPicker chartInfo={chartInfo} />
          )}
          {!isWordCloudGraph && <ChartTermCompare chartInfo={chartInfo} />}
          <TermValidateDisplay term={chartInfo.state.target.term} />
        </VFlex>
      </Pane>
      {!isWordCloudGraph && (
        <Pane title="Measured by">
          <ChartMetricSelect chartInfo={chartInfo} seriesIndex={0} />
        </Pane>
      )}
      {state.graph === CHART_GRAPH.COMBO_CHART && (
        <Pane title="Secondary Measure">
          <ChartMetricSelect chartInfo={chartInfo} seriesIndex={1} />
          <ChartComboValidateDisplay state={state} />
        </Pane>
      )}
      <Modal status={scaleOptionModalStatus} closeModal={closeScaleOptionModal}>
        <ScaleOptionModal
          scale={
            typeof state.series[scaleOptionModalState.seriesIndex].options
              ?.scale_min === 'number' &&
            typeof state.series[scaleOptionModalState.seriesIndex].options
              ?.scale_max === 'number'
              ? {
                  min:
                    state.series[scaleOptionModalState.seriesIndex].options
                      ?.scale_min || 0,
                  max:
                    state.series[scaleOptionModalState.seriesIndex].options
                      ?.scale_max || 0,
                }
              : undefined
          }
          onDoneClick={(scale) =>
            onScaleOptionChange(scaleOptionModalState.seriesIndex, scale)
          }
        />
      </Modal>
      <Modal status={colorScaleModalStatus} closeModal={closeColorScaleModal}>
        <ColorScaleModal
          scale={
            state.series[colorScaleModalState.seriesIndex].options?.color_scale
          }
          onDoneClick={(scale) =>
            onColorScaleChange(colorScaleModalState.seriesIndex, scale)
          }
        />
      </Modal>
    </>
  );
};
